import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ApiError, copyForCode } from '@wrapforce/shared/errors';
import * as Sentry from '@sentry/react';
import { useUpgradeDialogStore } from '@/stores/upgradeDialog';
import i18next from '@/lib/i18n';

// §27.3: a global MutationCache.onError renders the mapped toast for any mutation
// that didn't handle its own UI; QueryCache.onError covers background refetch
// failures, surfaced only when there's no cached data already on screen (§27.1).
// Only unexpected failures (network faults, INTERNAL) reach Sentry - known coded
// outcomes (429, validation, expired trial) are UX, not bugs (§28.1).
export function reportUnhandledError(error: unknown, opts?: { hasData?: boolean }): void {
  if (opts?.hasData) return;
  if (error instanceof ApiError) {
    // §23.4: every PLAN_EXPIRED re-opens the upgrade dialog instead of a toast
    // - the dialog *is* the answer to this specific error, so a toast on top
    // would be redundant noise on every blocked action during read-only lock.
    if (error.code === 'PLAN_EXPIRED') {
      useUpgradeDialogStore.getState().setOpen(true);
      return;
    }
    const copy = copyForCode(error.code);
    toast.error(i18next.t(`errors.${error.code}.title`, { ns: 'common', defaultValue: copy.title }), {
      description: i18next.t(`errors.${error.code}.body`, { ns: 'common', defaultValue: copy.body }),
    });
    if (error.code === 'INTERNAL') {
      Sentry.captureException(error, { tags: { requestId: error.requestId, code: error.code } });
    }
    return;
  }
  toast.error(i18next.t('errors.connectionHiccup.title', { ns: 'common' }), {
    description: i18next.t('errors.connectionHiccup.body', { ns: 'common' }),
  });
  Sentry.captureException(error);
}

function shouldRetry(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiError && (error.code === 'RATE_LIMITED' || error.status === 429)) {
    return false;
  }
  return failureCount < 1;
}

function retryDelay(attempt: number): number {
  return Math.min(1_000 * 2 ** attempt, 30_000);
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      gcTime: 10 * 60_000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: shouldRetry,
      retryDelay,
    },
    mutations: {
      retry: 0,
    },
  },
  queryCache: new QueryCache({
    onError: (error, query) => {
      reportUnhandledError(error, { hasData: query.state.data !== undefined });
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _vars, _ctx, mutation) => {
      // Mutations that already show their own inline error (auth forms, public
      // payment confirm) set meta.handlesOwnError to skip the global toast.
      if (mutation.meta?.handlesOwnError) return;
      reportUnhandledError(error);
    },
  }),
});
